import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Messages } from 'primereact/messages'
import { TabPanel, TabView } from 'primereact/tabview'

import { StatusTag } from '../../../../../../components/tableTemplates'
import { useToastContext } from '../../../../../../contexts/toast'
import {
  Invoice,
  InvoiceItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  ComparisonArray,
  areItemsModified,
  isOrderModified,
} from '../../../../../../utils/helper/checkConfirmationCodes'
import { getVisibleNotes } from '../../../../../../utils/helper/getVisibleNotes'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../ui-config/context'
import {
  requestPatchInvoiceById,
  requestPutInvoiceItemById,
  useAllInvoiceItems,
  useInvoiceItemById,
} from '../../../../data'
import { AllowanceCharges } from './AllowanceCharges'
import { EditForm } from './EditForm'

type Props = {
  visible: boolean
  setVisible: (params: boolean) => void
  setDisplayEditItemAllowanceCharge: (params: boolean) => void
  setIdEditItemAllowanceCharge: (params: string) => void
  setDisplayInfoItemAllowanceCharge: (params: boolean) => void
  setIdInfoItemAllowanceCharge: (params: string) => void
  invoiceId: string
  itemId: string
  setDisplayRejectItem: (params: boolean) => void
  setSelectedItems: any
  currency: string
  setEditModus: (params: boolean) => void
  editActiveTabViewPanelIndex: number
  setEditActiveTabViewPanelIndex: (params: number) => void
  setInfoActiveTabViewPanelIndex: (params: number) => void
}

type FormValues = {
  confirmationItemId: InvoiceItem['ConfirmationItemId']
  confirmationCountryOfOrigin: InvoiceItem['ConfirmationCountryOfOrigin']
  confirmationSellerProductId: InvoiceItem['ConfirmationSellerProductId']
  confirmationSellerProductDescription: InvoiceItem['ConfirmationSellerProductDescription']
  confirmationQuantity?: InvoiceItem['ConfirmationQuantity']
  confirmationUnit?: InvoiceItem['ConfirmationUnit']
  confirmationPriceBaseQuantity?: InvoiceItem['ConfirmationPriceBaseQuantity']
  confirmationPriceUnitOfMeasure?: InvoiceItem['ConfirmationPriceUnitOfMeasure']
  confirmationPrice?: InvoiceItem['ConfirmationPrice']
  confirmationPriceSum?: InvoiceItem['ConfirmationPriceSum']
  confirmationPriceSumChecked: boolean
  confirmationAllowanceChargeSum?: InvoiceItem['ConfirmationAllowanceChargeSum']
  confirmationLineItemAmount?: InvoiceItem['ConfirmationLineItemAmount']
  confirmationLineItemAmountChecked: boolean
  confirmationDeliveryDate?: Date
}
type FormFieldName = keyof FormValues

export const EditInvoiceItem: React.FC<Props> = ({
  visible,
  setVisible,
  setDisplayEditItemAllowanceCharge,
  setIdEditItemAllowanceCharge,
  setDisplayInfoItemAllowanceCharge,
  setIdInfoItemAllowanceCharge,
  invoiceId,
  itemId,
  setDisplayRejectItem,
  setSelectedItems,
  currency,
  setEditModus,
  editActiveTabViewPanelIndex,
  setEditActiveTabViewPanelIndex,
  setInfoActiveTabViewPanelIndex,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const messagesRef = useRef<Messages>(null)

  const { data: invoiceItems } = useAllInvoiceItems(invoiceId)
  const { data: invoiceItem, isLoading } = useInvoiceItemById(invoiceId, itemId)
  // const { data: invoiceItemAllowanceCharges } =
  //   useAllInvoiceItemAllowanceCharges(id, itemId)
  // const { mutate: globalMutate } = useSWRConfig()

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  useEffect(() => {
    if (!messagesRef) return

    const messages = getVisibleNotes(invoiceItem, 'INVOICE')

    if (!messages) return

    messages.forEach(note => {
      messagesRef.current?.replace({
        id: note.HintId,
        severity: 'warn',
        sticky: true,
        summary: t(`common.visibleNotes.${note.HintId}.summary`),
        detail: t(`common.visibleNotes.${note.HintId}.detail`),
        icon: PrimeIcons.EXCLAMATION_CIRCLE,
        closable: false,
      })
    })
  }, [invoiceItem, t, messagesRef])

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationItemId: invoiceItem?.ConfirmationItemId,
      confirmationCountryOfOrigin: invoiceItem?.ConfirmationCountryOfOrigin,
      confirmationSellerProductId: invoiceItem?.ConfirmationSellerProductId,
      confirmationSellerProductDescription:
        invoiceItem?.ConfirmationSellerProductDescription,
      confirmationQuantity: invoiceItem?.ConfirmationQuantity,
      confirmationUnit: invoiceItem?.ConfirmationUnit,
      confirmationPriceBaseQuantity: invoiceItem?.ConfirmationPriceBaseQuantity,
      confirmationPriceUnitOfMeasure:
        invoiceItem?.ConfirmationPriceUnitOfMeasure,
      confirmationPrice: invoiceItem?.ConfirmationPrice,
      confirmationPriceSum: invoiceItem?.ConfirmationPriceSum,
      confirmationPriceSumChecked:
        invoiceItem?.Meta?.Fields?.DisabledCalculation?.ConfirmationPriceSum ||
        false,
      confirmationAllowanceChargeSum:
        invoiceItem?.ConfirmationAllowanceChargeSum,
      confirmationLineItemAmount: invoiceItem?.ConfirmationLineItemAmount,
      confirmationLineItemAmountChecked:
        invoiceItem?.Meta?.Fields?.DisabledCalculation
          ?.ConfirmationLineItemAmount || false,
      confirmationDeliveryDate: invoiceItem?.ConfirmationDeliveryDate
        ? new Date(invoiceItem?.ConfirmationDeliveryDate)
        : undefined,
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationItemId',
          config: config.invoice.editInvoiceItem.confirmationItemId,
        },
        {
          fieldName: 'confirmationCountryOfOrigin',
          config: config.invoice.editInvoiceItem.confirmationCountryOfOrigin,
        },
        {
          fieldName: 'confirmationSellerProductId',
          config: config.invoice.editInvoiceItem.confirmationSellerProductId,
        },
        {
          fieldName: 'confirmationSellerProductDescription',
          config:
            config.invoice.editInvoiceItem.confirmationSellerProductDescription,
        },
        {
          fieldName: 'confirmationQuantity',
          config: config.invoice.editInvoiceItem.confirmationQuantity,
        },
        {
          fieldName: 'confirmationUnit',
          config: config.invoice.editInvoiceItem.confirmationUnit,
        },
        {
          fieldName: 'confirmationPriceBaseQuantity',
          config: config.invoice.editInvoiceItem.confirmationPriceBaseQuantity,
        },
        {
          fieldName: 'confirmationPriceUnitOfMeasure',
          config: config.invoice.editInvoiceItem.confirmationPriceUnitOfMeasure,
        },
        {
          fieldName: 'confirmationPrice',
          config: config.invoice.editInvoiceItem.confirmationPrice,
        },
        {
          fieldName: 'confirmationPriceSum',
          config: config.invoice.editInvoiceItem.confirmationPriceSum,
        },
        {
          fieldName: 'confirmationAllowanceChargeSum',
          config: config.invoice.editInvoiceItem.confirmationAllowanceChargeSum,
        },
        {
          fieldName: 'confirmationLineItemAmount',
          config: config.invoice.editInvoiceItem.confirmationLineItemAmount,
        },
        {
          fieldName: 'confirmationDeliveryDate',
          config: config.invoice.editInvoiceItem.confirmationDeliveryDate,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // submit form
        await submitForm(formData)

        // close the dialog and reset
        handleClose()

        // mutate()
        // globalMutate(`/invoices/${invoiceId}/items`)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'invoice.invoiceItems.table.edit.dialog.form.save.messages.success.summary',
          ),
          detail: t(
            'invoice.invoiceItems.table.edit.dialog.form.save.messages.success.detail',
            {
              orderPosition: invoiceItem?.OrderPosition,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setSelectedItems([]) // deselect the items in the table
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // check if items are modified
  function getConfirmationCode(
    formData: FormValues,
  ): InvoiceItem['ConfirmationCode'] {
    const comparisionArray: ComparisonArray = [
      {
        order: invoiceItem?.ItemId,
        confirmation: formData.confirmationItemId,
      },
      {
        order: invoiceItem?.CountryOfOrigin,
        confirmation: formData.confirmationCountryOfOrigin,
      },
      {
        order: invoiceItem?.SellerProductId,
        confirmation: formData.confirmationSellerProductId,
      },
      {
        order: invoiceItem?.SellerProductDescription,
        confirmation: formData.confirmationSellerProductDescription,
      },
      {
        order: invoiceItem?.Quantity,
        confirmation: formData.confirmationQuantity,
      },
      {
        order: invoiceItem?.Unit,
        confirmation: formData.confirmationUnit,
      },
      {
        order: invoiceItem?.PriceBaseQuantity,
        confirmation: formData.confirmationPriceBaseQuantity,
      },
      {
        order: invoiceItem?.PriceUnitOfMeasure,
        confirmation: formData.confirmationPriceUnitOfMeasure,
      },
      {
        order: invoiceItem?.Price,
        confirmation: formData.confirmationPrice,
      },
      {
        order: invoiceItem?.PriceSum,
        confirmation: formData.confirmationPriceSum,
      },
      {
        order: invoiceItem?.AllowanceChargeSum,
        confirmation: formData.confirmationAllowanceChargeSum,
      },
      {
        order: invoiceItem?.LineItemAmount,
        confirmation: formData.confirmationLineItemAmount,
      },
      {
        order: new Date(invoiceItem?.DeliveryDate!).toDateString(),
        confirmation: formData.confirmationDeliveryDate?.toDateString(),
      },
    ]

    return areItemsModified(comparisionArray) ? 'MODIFIED' : 'NOT_SET'
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    // new invoice item
    const newInvoiceItem: InvoiceItem = produce(invoiceItem!, draft => {
      draft.ConfirmationItemId = formData.confirmationItemId
      draft.ConfirmationCountryOfOrigin = formData.confirmationCountryOfOrigin
      draft.ConfirmationSellerProductId = formData.confirmationSellerProductId
      draft.ConfirmationSellerProductDescription =
        formData.confirmationSellerProductDescription
      draft.ConfirmationQuantity = formData.confirmationQuantity as number
      draft.ConfirmationUnit = formData.confirmationUnit as string
      draft.ConfirmationPriceBaseQuantity =
        formData.confirmationPriceBaseQuantity as number
      draft.ConfirmationPriceUnitOfMeasure =
        formData.confirmationPriceUnitOfMeasure
      draft.ConfirmationPrice = formData.confirmationPrice as number
      draft.ConfirmationPriceSum = formData.confirmationPriceSum as number
      draft.Meta!.Fields!.DisabledCalculation!.ConfirmationPriceSum =
        formData.confirmationPriceSumChecked
      draft.ConfirmationAllowanceChargeSum =
        formData.confirmationAllowanceChargeSum
      draft.ConfirmationLineItemAmount = formData.confirmationLineItemAmount
      draft.Meta!.Fields!.DisabledCalculation!.ConfirmationLineItemAmount =
        formData.confirmationLineItemAmountChecked
      draft.ConfirmationDeliveryDate =
        formData.confirmationDeliveryDate?.toISOString()
      draft.ConfirmationCode = getConfirmationCode(formData)
    })

    // patch for ConfirmationCode
    const newDataArray: InvoiceItem[] =
      invoiceItems?.map(item =>
        item.Id === newInvoiceItem.Id ? newInvoiceItem : item,
      ) || []

    const notSetCode: Invoice['ConfirmationCode'] = 'NOT_SET'
    const modifiedCode: Invoice['ConfirmationCode'] = 'MODIFIED'
    const patchData: PatchRequest<WebEdiDto<Invoice>> = [
      {
        op: 'replace',
        path: '/Payload/ConfirmationCode',
        value: isOrderModified(newDataArray!) ? modifiedCode : notSetCode,
      },
    ]
    // patch for invoice
    await requestPatchInvoiceById(invoiceId, patchData)
    await requestPutInvoiceItemById(invoiceId, itemId, newInvoiceItem)
  }

  // handleReset
  const handleReset = async () => {
    await formik.setFieldValue('confirmationItemId', invoiceItem?.ItemId)
    await formik.setFieldValue(
      'confirmationCountryOfOrigin',
      invoiceItem?.CountryOfOrigin,
    )
    await formik.setFieldValue(
      'confirmationSellerProductId',
      invoiceItem?.SellerProductId,
    )
    await formik.setFieldValue(
      'confirmationSellerProductDescription',
      invoiceItem?.SellerProductDescription,
    )
    await formik.setFieldValue('confirmationQuantity', invoiceItem?.Quantity)
    await formik.setFieldValue('confirmationUnit', invoiceItem?.Unit)
    await formik.setFieldValue(
      'confirmationPriceBaseQuantity',
      invoiceItem?.PriceBaseQuantity,
    )
    await formik.setFieldValue(
      'confirmationPriceUnitOfMeasure',
      invoiceItem?.PriceUnitOfMeasure,
    )
    await formik.setFieldValue('confirmationPrice', invoiceItem?.Price)
    await formik.setFieldValue('confirmationPriceSum', invoiceItem?.PriceSum)
    await formik.setFieldValue('confirmationPriceSumChecked', false)
    await formik.setFieldValue(
      'confirmationAllowanceChargeSum',
      invoiceItem?.AllowanceChargeSum,
    )
    await formik.setFieldValue(
      'confirmationLineItemAmount',
      invoiceItem?.LineItemAmount,
    )
    await formik.setFieldValue('confirmationLineItemAmountChecked', false)
    await formik.setFieldValue(
      'confirmationDeliveryDate',
      invoiceItem?.ConfirmationDeliveryDate
        ? new Date(invoiceItem.ConfirmationDeliveryDate)
        : undefined,
    )
  }

  // handleClose
  const handleClose = () => {
    formik.resetForm()
    setVisible(false)
    setEditActiveTabViewPanelIndex(0)
    setInfoActiveTabViewPanelIndex(0)
  }

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t('invoice.invoiceItems.table.edit.dialog.title', {
            position: invoiceItem?.OrderPosition,
          })}
        </span>

        <StatusTag status={getConfirmationCode(formik.values)} />
      </div>
    </>
  )

  // footer
  const footer = (
    <div className="flex flex-wrap justify-content-between pt-3">
      <Button
        className="p-button-text w-full md:w-auto"
        label={t('common.buttons.reset')}
        icon="pi pi-replay"
        onClick={handleReset}
        type="reset"
        disabled={
          getConfirmationCode(formik.values) === 'MODIFIED' ? false : true
        }
      />

      <Button
        className="p-button-text ml-auto w-full md:w-auto mb-1 md:mb-0"
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        onClick={handleClose}
        type="button"
      />

      <Button
        label={t('common.buttons.save')}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
        onClick={formik.submitForm}
        disabled={!formik.dirty}
        loading={isRequestRunning}
      />
    </div>
  )

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      <Messages ref={messagesRef} />
      <TabView activeIndex={editActiveTabViewPanelIndex}>
        <TabPanel
          header={t('invoice.invoiceItems.table.edit.dialog.form.title')}
        >
          <EditForm
            currency={currency}
            formik={formik}
            isFormFieldValid={isFormFieldValid}
            getFormErrorMessage={getFormErrorMessage}
            invoiceItem={invoiceItem!}
            setIsRequestRunning={setIsRequestRunning}
            isLoading={isLoading}
          />
        </TabPanel>
        {config.invoice.editInvoiceItem.allowanceCharges.enabled !== false && (
          <TabPanel
            header={t(
              'invoice.invoiceItems.table.edit.dialog.allowanceCharges.title',
            )}
          >
            <AllowanceCharges
              setEditModus={setEditModus}
              setDisplayEditItem={setVisible}
              setDisplayEditItemAllowanceCharge={
                setDisplayEditItemAllowanceCharge
              }
              setIdEditItemAllowanceCharge={setIdEditItemAllowanceCharge}
              setDisplayInfoItemAllowanceCharge={
                setDisplayInfoItemAllowanceCharge
              }
              setIdInfoItemAllowanceCharge={setIdInfoItemAllowanceCharge}
              setEditActiveTabViewPanelIndex={setEditActiveTabViewPanelIndex}
              setInfoActiveTabViewPanelIndex={setInfoActiveTabViewPanelIndex}
              invoiceId={invoiceId}
              itemId={itemId}
              currency={currency}
              data={invoiceItem?.AllowanceCharges}
            />
          </TabPanel>
        )}
      </TabView>
    </Dialog>
  )
}
